/**
 * User administration (CLAUDE.md 12.2).
 *
 * The one design point worth defending: an administrator gets a **password reset
 * link**, never the ability to set a password. This is software whose entire premise is
 * attributable professional responsibility (section 2) — a calculation is signed by the
 * engineer who ran it. A password an administrator chose and knows breaks that chain,
 * because "who ran this" stops having a single answer.
 *
 * The self-lockout guards live on the server (`app/api/admin.py`) and differ by action,
 * so the controls do too: deactivating your own account is refused outright and the
 * button is disabled, while *stepping down* is allowed as long as another active
 * administrator remains — so that select stays enabled and the server explains itself
 * if you are the last one.
 */
import { useState } from 'react'
import { Copy, KeyRound, LogOut, Unlock } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
  PAGE_SIZE,
  useAdminUsers,
  useMintPasswordResetLink,
  useRevokeUserSessions,
  useUpdateAdminUser,
} from '@/api/admin'
import { ApiError } from '@/api/client'
import type { AdminUser, PasswordResetLink } from '@/api/types'
import { useAuth } from '@/auth/AuthProvider'
import {
  Pagination,
  Pill,
  TableScroller,
  Td,
  Th,
  formatDateTime,
} from '@/components/admin/AdminShell'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useToast } from '@/components/ui/toast'

const selectClass = 'h-11 rounded-md border border-border bg-surface px-3 text-text'

/** The fields a row can change. `userId` is supplied by the caller, not the row. */
interface UserPatch {
  is_active?: boolean
  system_role?: 'user' | 'sysadmin'
  display_name?: string
  unlock?: boolean
  require_password_change?: boolean
}

export function AdminUsers() {
  const { t } = useTranslation(['admin', 'common'])
  const { toast } = useToast()
  const { user: me } = useAuth()

  const [q, setQ] = useState('')
  const [role, setRole] = useState('')
  const [offset, setOffset] = useState(0)
  const [resetLink, setResetLink] = useState<PasswordResetLink | null>(null)

  const users = useAdminUsers({ q: q || undefined, system_role: role || undefined, offset })
  const update = useUpdateAdminUser()
  const mintLink = useMintPasswordResetLink()
  const revokeSessions = useRevokeUserSessions()

  const fail = (error: unknown) =>
    toast({
      title: t('common.error', { ns: 'common' }),
      description: error instanceof ApiError ? error.localised : undefined,
      variant: 'error',
    })

  const patch = (userId: number, body: UserPatch) =>
    update.mutate(
      { ...body, userId },
      {
        onSuccess: () => toast({ title: t('common.saved', { ns: 'common' }), variant: 'success' }),
        onError: fail,
      },
    )

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">{t('admin.users.title')}</h2>

      <div className="flex flex-wrap gap-2">
        <Input
          className="max-w-sm flex-1"
          placeholder={t('admin.users.search')}
          value={q}
          onChange={(event) => {
            setQ(event.target.value)
            setOffset(0)
          }}
        />
        <select
          className={selectClass}
          aria-label={t('admin.users.filter_role')}
          value={role}
          onChange={(event) => {
            setRole(event.target.value)
            setOffset(0)
          }}
        >
          <option value="">{t('common.all', { ns: 'common' })}</option>
          <option value="user">{t('admin.users.role.user')}</option>
          <option value="sysadmin">{t('admin.users.role.sysadmin')}</option>
        </select>
      </div>

      {users.data && users.data.items.length === 0 ? (
        <p className="rounded-lg border border-border p-6 text-center text-sm text-text-muted">
          {t('admin.users.empty')}
        </p>
      ) : (
        <>
          <TableScroller>
            <thead>
              <tr>
                <Th>{t('common.name', { ns: 'common' })}</Th>
                <Th>{t('admin.users.role')}</Th>
                <Th>{t('common.status', { ns: 'common' })}</Th>
                <Th>{t('admin.users.last_login')}</Th>
                <Th className="text-end">{t('common.actions', { ns: 'common' })}</Th>
              </tr>
            </thead>
            <tbody>
              {users.data?.items.map((row) => (
                <UserRow
                  key={row.id}
                  row={row}
                  isSelf={row.id === me?.id}
                  onPatch={patch}
                  onResetLink={() =>
                    mintLink.mutate(row.id, { onSuccess: setResetLink, onError: fail })
                  }
                  onRevokeSessions={() =>
                    revokeSessions.mutate(row.id, {
                      onSuccess: (result) =>
                        toast({
                          title: t('admin.users.revoked_sessions', { count: result.revoked }),
                          variant: 'success',
                        }),
                      onError: fail,
                    })
                  }
                />
              ))}
            </tbody>
          </TableScroller>

          <Pagination
            total={users.data?.total ?? 0}
            limit={PAGE_SIZE}
            offset={offset}
            onChange={setOffset}
          />
        </>
      )}

      <ResetLinkDialog link={resetLink} onClose={() => setResetLink(null)} />
    </div>
  )
}

function UserRow({
  row,
  isSelf,
  onPatch,
  onResetLink,
  onRevokeSessions,
}: {
  row: AdminUser
  isSelf: boolean
  onPatch: (userId: number, body: UserPatch) => void
  onResetLink: () => void
  onRevokeSessions: () => void
}) {
  const { t } = useTranslation(['admin', 'common'])
  const locked = row.locked_until !== null

  return (
    <tr>
      <Td>
        <div className="font-medium">{row.display_name}</div>
        <div className="text-xs text-text-muted">
          {row.username} · {row.email}
        </div>
      </Td>
      <Td>
        <select
          className="h-9 rounded-md border border-border bg-surface px-2 text-sm text-text disabled:opacity-50"
          aria-label={t('admin.users.role')}
          value={row.system_role}
          // Stepping down is permitted, so this stays enabled even for yourself. The
          // server refuses it only when you are the last active administrator, and
          // returns a message saying so — a rule better stated than silently enforced
          // by a greyed-out control.
          onChange={(event) =>
            onPatch(row.id, { system_role: event.target.value as 'user' | 'sysadmin' })
          }
        >
          <option value="user">{t('admin.users.role.user')}</option>
          <option value="sysadmin">{t('admin.users.role.sysadmin')}</option>
        </select>
      </Td>
      <Td>
        <div className="flex flex-col items-start gap-1">
          {row.is_active ? (
            <Pill tone="pass">{t('admin.users.active')}</Pill>
          ) : (
            <Pill tone="fail">{t('admin.users.inactive')}</Pill>
          )}
          {locked && (
            <Pill tone="warn">
              {t('admin.users.locked_until', { date: formatDateTime(row.locked_until) })}
            </Pill>
          )}
        </div>
      </Td>
      <Td className="whitespace-nowrap text-text-muted">
        {row.last_login_at ? formatDateTime(row.last_login_at) : t('admin.users.never')}
        <div className="text-xs">
          {t('admin.users.sessions')}: {row.active_sessions} · {t('admin.users.workspaces')}:{' '}
          {row.workspace_count}
        </div>
      </Td>
      <Td>
        <div className="flex flex-wrap justify-end gap-1">
          {locked && (
            <Button size="sm" onClick={() => onPatch(row.id, { unlock: true })}>
              <Unlock size={14} aria-hidden />
              {t('admin.users.unlock')}
            </Button>
          )}
          <Button size="sm" onClick={onResetLink}>
            <KeyRound size={14} aria-hidden />
            {t('admin.users.reset_link')}
          </Button>
          {row.active_sessions > 0 && (
            <Button size="sm" onClick={onRevokeSessions}>
              <LogOut size={14} aria-hidden />
              {t('admin.users.revoke_sessions')}
            </Button>
          )}
          <Button
            size="sm"
            variant={row.is_active ? 'danger' : 'secondary'}
            // Deactivating yourself signs you out with no way back in to undo it, so
            // it is refused outright rather than guarded by a count.
            disabled={isSelf && row.is_active}
            onClick={() => onPatch(row.id, { is_active: !row.is_active })}
          >
            {row.is_active ? t('admin.users.deactivate') : t('admin.users.reactivate')}
          </Button>
        </div>
      </Td>
    </tr>
  )
}

/**
 * Shown once. The token is stored hashed, so there is no "show it again" action to
 * build later — the same contract as a share link (11.4).
 */
function ResetLinkDialog({
  link,
  onClose,
}: {
  link: PasswordResetLink | null
  onClose: () => void
}) {
  const { t } = useTranslation(['admin', 'common'])
  const { toast } = useToast()

  return (
    <Dialog open={link !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('admin.users.reset_link')}</DialogTitle>
          <DialogDescription>{t('admin.users.reset_link_hint')}</DialogDescription>
        </DialogHeader>

        <p className="text-sm text-[var(--status-warn)]">{t('admin.users.reset_link_copy')}</p>
        <div className="flex gap-2">
          <Input readOnly value={link?.url ?? ''} className="font-mono text-xs" />
          <Button
            onClick={() => {
              void navigator.clipboard
                .writeText(link?.url ?? '')
                .then(() =>
                  toast({ title: t('action.copy', { ns: 'common' }), variant: 'success' }),
                )
            }}
          >
            <Copy size={15} aria-hidden />
            {t('action.copy', { ns: 'common' })}
          </Button>
        </div>
        <p className="text-xs text-text-muted">
          {t('admin.users.reset_link_expires', { date: formatDateTime(link?.expires_at ?? null) })}
        </p>
      </DialogContent>
    </Dialog>
  )
}
